import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { QRCodeSVG } from 'qrcode.react';
import { dynamicQrService } from '../services/dynamicQr';
import { toast } from '../hooks/use-toast';

const MyQR = () => {
  const [qrData, setQrData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  // Función para manejar la expiración del QR
  const handleExpiration = useCallback(async () => {
    setIsExpired(true);
    
    // Notificar al backend que el QR expiró
    if (qrData?.token) {
      try {
        await dynamicQrService.expire(qrData.token);
      } catch (err) {
        console.error('Error marcando QR como expirado:', err);
      }
    }

    // Mostrar alerta
    toast({
      title: "QR Caducado",
      description: "Tu código QR ha caducado. Se generará uno nuevo automáticamente.",
      variant: "default",
    });

    // Ocultar QR y volver a estado inicial (pantalla negra + botón)
    setQrData(null);
    setTimeLeft(0);
  }, [qrData?.token]);

  // Cargar o generar QR al montar el componente
  useEffect(() => {
    loadQR();
  }, []);

  // Contador regresivo
  // IMPORTANTE: Recalcular desde expires_at cada segundo para evitar desfases
  useEffect(() => {
    if (!qrData || !qrData.expiresAt) {
      return;
    }

    const timer = setInterval(() => {
      // Recalcular tiempo restante desde expires_at (fuente de verdad)
      // Esto evita problemas de desfase por delays en el setInterval
      const now = new Date();
      const expires = new Date(qrData.expiresAt);
      const secondsLeft = Math.max(0, Math.floor((expires - now) / 1000));
      
      if (secondsLeft <= 0) {
        handleExpiration();
        setTimeLeft(0);
      } else {
        setTimeLeft(secondsLeft);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [qrData?.expiresAt, handleExpiration]);

  const loadQR = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Solo consultar estado (NO generar automáticamente)
      const response = await dynamicQrService.getStatus();
      
      if (response.success && response.has_active_qr && response.data) {
        const { token, expires_at, remaining_seconds } = response.data;
        
        // Log de debug para verificar valores recibidos
        console.log('[MyQR][DEBUG] QR recibido del backend', {
          expires_at,
          remaining_seconds_from_backend: remaining_seconds,
        });
        
        setQrData({
          token,
          expiresAt: new Date(expires_at),
        });
        
        // Calcular tiempo restante basado en expires_at (fuente de verdad)
        // Usar expires_at del backend para evitar problemas de zona horaria
        const now = new Date();
        const expires = new Date(expires_at);
        const secondsLeft = Math.max(0, Math.floor((expires - now) / 1000));
        
        // Log de debug para comparar cálculos
        console.log('[MyQR][DEBUG] Cálculo de tiempo restante', {
          now: now.toISOString(),
          expires_at: expires.toISOString(),
          remaining_seconds_backend: remaining_seconds,
          remaining_seconds_calculated: secondsLeft,
          difference: Math.abs(remaining_seconds - secondsLeft),
        });
        
        // Usar el valor calculado desde expires_at (más confiable)
        setTimeLeft(secondsLeft);
        setIsExpired(false);
      } else {
        // No hay QR activo - mostrar pantalla negra + botón
        setQrData(null);
        setTimeLeft(0);
        setIsExpired(false);
      }
    } catch (err) {
      console.error('Error consultando estado de QR:', err);
      setError(err.message || 'Error al consultar el estado del QR');
      
      // Si el error es por suscripción, mostrar mensaje específico
      if (err.message && err.message.includes('suscripción')) {
        toast({
          title: "Suscripción requerida",
          description: err.message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const generateQR = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await dynamicQrService.generate();
      
      if (response.success && response.data) {
        const { token, expires_at, remaining_seconds } = response.data;
        
        // Log de debug para verificar valores recibidos
        console.log('[MyQR][DEBUG] QR generado - datos recibidos', {
          expires_at,
          remaining_seconds_from_backend: remaining_seconds,
        });
        
        setQrData({
          token,
          expiresAt: new Date(expires_at),
        });
        
        // Calcular tiempo restante basado en expires_at (fuente de verdad)
        const now = new Date();
        const expires = new Date(expires_at);
        const secondsLeft = Math.max(0, Math.floor((expires - now) / 1000));
        
        // Log de debug para comparar cálculos
        console.log('[MyQR][DEBUG] Cálculo de tiempo restante (generar)', {
          now: now.toISOString(),
          expires_at: expires.toISOString(),
          remaining_seconds_backend: remaining_seconds,
          remaining_seconds_calculated: secondsLeft,
          difference: Math.abs(remaining_seconds - secondsLeft),
        });
        
        setTimeLeft(secondsLeft);
        setIsExpired(false);
      } else {
        throw new Error(response.error || 'Error al generar QR');
      }
    } catch (err) {
      console.error('Error generando QR:', err);
      setError(err.message || 'No puedes generar un QR porque tu suscripción no está activa');
      
      toast({
        title: "Error",
        description: err.message || 'No puedes generar un QR porque tu suscripción no está activa',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };


  const regenerateQR = async () => {
    // Si hay un QR activo, marcarlo como expirado primero
    if (qrData?.token) {
      try {
        await dynamicQrService.expire(qrData.token);
      } catch (err) {
        console.error('Error expirando QR anterior:', err);
      }
    }

    // Generar nuevo QR
    await generateQR();
  };

  // Calcular progreso basado en el tiempo restante
  // NOTA: Como no tenemos el tiempo total original guardado, usamos una estimación
  // basada en el tiempo restante. El progreso se calcula de forma inversa:
  // - Más tiempo restante = menos progreso
  // - Menos tiempo restante = más progreso
  // 
  // Esto es una aproximación visual, el tiempo real de expiración se basa en expires_at
  const calculateProgress = () => {
    if (!qrData || timeLeft <= 0) return 100; // Si expiró, mostrar 100%
    
    // Estimar tiempo total basado en timeLeft
    // Asumimos que el QR tiene al menos el doble del tiempo restante como duración total
    // Esto es una estimación conservadora para mostrar el progreso visualmente
    // El tiempo real de expiración se controla por expires_at, no por este cálculo
    const estimatedTotalSeconds = Math.max(timeLeft * 2, 60); // Mínimo 60 segundos estimados
    const elapsed = estimatedTotalSeconds - timeLeft;
    const progress = (elapsed / estimatedTotalSeconds) * 100;
    
    return Math.max(0, Math.min(100, progress));
  };

  const progress = calculateProgress();

  // Formatear tiempo restante
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="px-6 py-8">
        <div className="max-w-md mx-auto space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-2">Identidad</h2>
            <p className="text-[#A2A2A2]">Cargando tu código QR...</p>
          </div>
          <div className="bg-white rounded-3xl p-8 flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-black animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-8">
      <div className="max-w-md mx-auto space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-2">Identidad</h2>
          <p className="text-[#A2A2A2]">
            Muestra tu QR para reclamar tu beneficio
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-3xl p-6 space-y-4">
            <div className="flex items-center gap-3 text-red-400">
              <AlertCircle className="w-5 h-5" />
              <p className="text-white">{error}</p>
            </div>
            <Button
              onClick={loadQR}
              className="w-full"
            >
              Intentar nuevamente
            </Button>
          </div>
        )}

        {/* QR Card o Pantalla negra + botón */}
        {!error && !isExpired && qrData ? (
          <div className="bg-white rounded-3xl p-8 space-y-6">
            {/* QR Code */}
            <div className="aspect-square bg-white rounded-2xl flex items-center justify-center p-4">
              <QRCodeSVG
                value={qrData.token}
                size={256}
                level="H"
                includeMargin={true}
              />
            </div>

            {/* Timer */}
            <div className="space-y-3">
              <p className="text-black text-center font-medium">
                Este código expira en {formatTime(timeLeft)}
              </p>
              
              {/* Progress Bar */}
              <div className="w-full h-2 bg-black/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-black transition-all duration-1000"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            {/* Regenerate Button */}
            <Button
              onClick={regenerateQR}
              variant="outline"
              className="w-full border-black/20 text-black bg-transparent hover:bg-black/5 rounded-full py-4 transition-all hover:scale-105 active:scale-95"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar QR
            </Button>
          </div>
        ) : !error && !isExpired && !qrData ? (
          // Pantalla negra + botón "Ver mi QR 👀"
          <div className="bg-black border border-white/10 rounded-3xl p-8 space-y-6">
            <div className="aspect-square bg-black/50 rounded-2xl flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="w-24 h-24 mx-auto bg-white/10 rounded-full flex items-center justify-center">
                  <RefreshCw className="w-12 h-12 text-white/50" />
                </div>
                <p className="text-white/50 text-sm">No hay código QR activo</p>
              </div>
            </div>
            <Button
              onClick={generateQR}
              disabled={loading}
              className="w-full bg-white text-black hover:bg-white/90 rounded-full py-6 transition-all hover:scale-105 active:scale-95"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  👀 Ver mi QR
                </>
              )}
            </Button>
          </div>
        ) : isExpired ? (
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 space-y-4 text-center">
            <p className="text-white mb-2">Tu código QR ha expirado</p>
            <Button
              onClick={generateQR}
              disabled={loading}
              className="w-full bg-white text-black hover:bg-white/90 rounded-full py-6 transition-all hover:scale-105 active:scale-95"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  👀 Ver mi QR
                </>
              )}
            </Button>
          </div>
        ) : null}

        {/* Mensaje breve sobre expiración (reemplaza la sección "Cómo usarlo") */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <p className="text-[#A2A2A2] text-sm text-center">
            Este código QR expira cada 60 segundos. Debes tener tu beneficio activo para que sea válido. 
            Si ya expiró, vuelve a tocar en "Ver mi QR 👀" para generar uno nuevo.
          </p>
          <p className="text-[#A2A2A2] text-sm text-center mt-2 font-bold">
            Válido solo en Tiendas participantes y aplica restricciones
          </p>
        </div>
      </div>
    </div>
  );
};

export default MyQR;
